import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../types/user';
import { LoginRequest, SessionSettings } from '../types/auth';
import { AuthService } from '../services/auth';
import StorageService from '../services/storage';
import { API_BASE_URL } from '../services/api';
import { LogoutCoordinator } from '../services/logoutCoordinator';
import { coordinatedRefresh } from '../services/refreshCoordinator';
import { DEBUG_LOGS } from '../utils/debugFlags';

import { HospitalInfo } from '../types/auth';

interface PendingLogoutRedirect {
  path: string;
  error: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  validationState: 'idle' | 'validating' | 'valid' | 'invalid' | 'failed';
  loading: boolean;
  error: string | null;
  retryCount: number;
  lastValidation: number | null;
  networkError: boolean;
  sessionSettings: SessionSettings | null;
  hospital: HospitalInfo | null;
  accessibleHospitals: HospitalInfo[];
  pendingLogoutRedirect: PendingLogoutRedirect | null;
}

// Initialize state from localStorage
const getInitialState = (): AuthState => {
  const token = StorageService.getToken();
  const user = StorageService.getUser();
  const isTokenExpired = StorageService.isTokenExpired();
  const sessionSettings = StorageService.getSessionSettings();
  
  // Clear expired tokens
  if (token && isTokenExpired) {
    StorageService.clearAuthData();
    return {
      user: null,
      token: null,
      isAuthenticated: false,
      validationState: 'idle',
      loading: false,
      error: null,
      retryCount: 0,
      lastValidation: null,
      networkError: false,
      sessionSettings: null,
      hospital: null,
      accessibleHospitals: [],
      pendingLogoutRedirect: null,
    };
  }

  const hospital = StorageService.getHospital ? StorageService.getHospital() : null;
  const accessibleHospitals = StorageService.getAccessibleHospitals ? StorageService.getAccessibleHospitals() : [];
  
  return {
    user,
    token,
    isAuthenticated: !!(token && user && !isTokenExpired),
    validationState: 'idle',
    loading: false,
    error: null,
    retryCount: 0,
    lastValidation: null,
    networkError: false,
    sessionSettings,
    hospital: hospital || null,
    accessibleHospitals: accessibleHospitals || [],
    pendingLogoutRedirect: null,
  };
};

const initialState: AuthState = getInitialState();

export const loginAsync = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await AuthService.login(credentials);
      return response;
    } catch (error: any) {
      // Extract user-friendly error message from backend response
      let errorMessage = 'Login failed';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        // Check for specific error message from backend
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
      } else if (error.message === 'Network Error') {
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

export const logoutAsync = createAsyncThunk('auth/logout', async (_, { getState }) => {
  const state = (getState() as any).auth;
  console.warn(
    `[LOGOUT-DEBUG] authSlice.logoutAsync | reason=logoutAsync_dispatched | ` +
    `user=${state.user?.username ?? 'null'} | userId=${state.user?.id ?? 'null'} | ` +
    `wasAuthenticated=${state.isAuthenticated} | timestamp=${new Date().toISOString()}`
  );
  console.trace('[LOGOUT-DEBUG] authSlice.logoutAsync stack trace');
  await AuthService.logout();
});


interface RehydrateAuthOptions {
  forceServerValidation?: boolean;
}

// Enhanced rehydrate auth with retry logic and better error handling
export const rehydrateAuth = createAsyncThunk(
  'auth/rehydrate',
  async (options: RehydrateAuthOptions | undefined, { rejectWithValue }) => {
    const forceServerValidation = options?.forceServerValidation ?? false;
    const user = StorageService.getUser();
    
    // Check if we have cached validation that's still fresh (less than 5 minutes)
    const lastValidation = StorageService.getLastValidation();
    const now = Date.now();
    const sessionSettings = StorageService.getSessionSettings();
    const validationCacheTime = Math.max(1, sessionSettings?.session_check_interval_minutes ?? 5) * 60 * 1000;
    
    if (!forceServerValidation && user && lastValidation && (now - lastValidation) < validationCacheTime) {
      return { token: 'cookie', user, fromCache: true };
    }
    
    // If we have user data, validate with server (cookie-based auth)
    if (user) {
      let timeoutId: NodeJS.Timeout | undefined;
      try {
        
        // Make a request to validate current authentication status
        // Create browser-compatible timeout signal
        const abortController = new AbortController();
        timeoutId = setTimeout(() => {
          abortController.abort();
        }, 5000); // 5 second timeout

        // Use the full API URL like all other services
        const response = await fetch(`${API_BASE_URL}/users/me/`, {
          method: 'GET',
          credentials: 'include', // Include cookies
          headers: {
            'Content-Type': 'application/json',
          },
          // Add timeout to prevent hanging requests
          signal: abortController.signal
        });

        // Clear timeout since request completed
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const validatedUser = await response.json();
          // Cache successful validation
          StorageService.setLastValidation(now);
          return { token: 'cookie', user: validatedUser, fromCache: false };
        } else if (response.status === 401 || response.status === 403) {
          // The access cookie was rejected. Before destroying the session on
          // startup, try a token refresh — the access token may simply be
          // expired/rotated while the session is still refreshable (this mirrors
          // the periodic validator, which refreshes before logging out; the
          // startup path previously skipped it and logged users out on hard
          // refresh even when recovery was possible). Only a 401 is refreshable;
          // a 403 is a permission problem, not an expiry.
          if (response.status === 401) {
            try {
              const refresh = await coordinatedRefresh();
              if (refresh.ok) {
                const retryResp = await fetch(`${API_BASE_URL}/users/me/`, {
                  method: 'GET',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                });
                if (retryResp.ok) {
                  const validatedUser = await retryResp.json();
                  StorageService.setLastValidation(Date.now());
                  return { token: 'cookie', user: validatedUser, fromCache: false };
                }
                // Refresh succeeded but the re-validation still failed. Only an
                // explicit 401/403 means the session is genuinely gone (clear
                // below). Any other status is a transient server hiccup — keep
                // the session and retry rather than forcing a logout.
                if (retryResp.status !== 401 && retryResp.status !== 403) {
                  return rejectWithValue({
                    message: `Server error during validation: ${retryResp.status}`,
                    isAuthError: false,
                    isRetryable: true,
                  });
                }
              } else if (refresh.status === 0 || refresh.status >= 500) {
                // Transient refresh failure — offline, a gateway blip, or a
                // backend restart, typically right as a mobile tab resumes.
                // This is NOT an auth rejection (coordinatedRefresh already
                // retried 5xx/network internally and reports status 0 for a
                // network error). Keep the session and let the retry logic
                // recover instead of logging the user out spuriously.
                return rejectWithValue({
                  message: 'Network error during session refresh',
                  isAuthError: false,
                  isNetworkError: refresh.status === 0,
                  isRetryable: true,
                });
              }
              // Otherwise refresh returned an explicit 401/403 → genuine
              // expiry; fall through to clear the session below.
            } catch (_) {
              // Unexpected exception during refresh — be conservative and keep
              // the session (retryable) rather than logging the user out.
              return rejectWithValue({
                message: 'Network error during session refresh',
                isAuthError: false,
                isNetworkError: true,
                isRetryable: true,
              });
            }
          }
          // Refresh did not recover the session — genuine auth failure, clear data.
          StorageService.clearAuthData();
          return rejectWithValue({
            message: 'Authentication expired',
            isAuthError: true,
            status: response.status
          });
        } else {
          // Server error - don't clear auth data, allow retry
          return rejectWithValue({ 
            message: `Server error: ${response.status}`, 
            isAuthError: false,
            status: response.status,
            isRetryable: true
          });
        }
      } catch (error: any) {
        // Clear timeout in case of error
        if (typeof timeoutId !== 'undefined') {
          clearTimeout(timeoutId);
        }
        
        // Check if it's a network/timeout error
        if (error.name === 'AbortError' || error instanceof TypeError || error.message.includes('fetch')) {
          // Network error - keep existing auth state and allow retry
          return rejectWithValue({ 
            message: 'Network error during validation', 
            isAuthError: false,
            isNetworkError: true,
            isRetryable: true
          });
        }
        
        // Unknown error - be conservative and keep auth state
        return rejectWithValue({ 
          message: 'Unknown error during validation', 
          isAuthError: false,
          isRetryable: true
        });
      }
    }
    
    // No user data found - this is normal for first-time visitors
    return rejectWithValue({ 
      message: 'No authentication data found', 
      isAuthError: true,
      isInitialLoad: true // Flag to indicate this is from initial load, not a failed login
    });
  }
);

// Action to retry authentication validation with exponential backoff
export const retryAuthValidation = createAsyncThunk(
  'auth/retryValidation',
  async (_, { getState, dispatch }) => {
    const state = getState() as { auth: AuthState };
    const retryCount = state.auth.retryCount;
    
    // Calculate delay with exponential backoff (1s, 2s, 4s)
    const delay = Math.min(1000 * Math.pow(2, retryCount), 8000);
    
    
    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // Retry the validation
    return dispatch(rehydrateAuth({ forceServerValidation: true }));
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      // Keep localStorage in sync with Redux
      localStorage.setItem('hospital_user', JSON.stringify(action.payload));
    },
    // PHASE 6: Complete two-step login
    completeTwoStepLogin: (state, action: PayloadAction<{
      user: User;
      token: string;
      hospital: any;
      accessible_hospitals: any[];
      session_settings?: SessionSettings | null;
    }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
      state.validationState = 'valid';  // Must be 'valid', not 'validated'
      state.hospital = action.payload.hospital;
      state.accessibleHospitals = action.payload.accessible_hospitals;
      state.lastValidation = Date.now();
      state.sessionSettings = action.payload.session_settings ?? StorageService.getSessionSettings();
      // Clear any stale logout lock from a previous failed session — fresh login
      // means the user has authenticated successfully and any prior auth-failure
      // gating should be released.
      state.pendingLogoutRedirect = null;
      LogoutCoordinator.reset();
      if (DEBUG_LOGS && action.payload.session_settings) {
        const s = action.payload.session_settings;
        console.log(
          `[SESSION] completeTwoStepLogin stored session_settings: idle_timeout_minutes=${s.idle_timeout_minutes ?? '?'} max_login_duration_minutes=${s.max_login_duration_minutes ?? '?'} session_check_interval_minutes=${s.session_check_interval_minutes ?? '?'}`
        );
      }
      // Ensure localStorage is in sync (defense-in-depth even though persistLoginState should have done this)
      localStorage.setItem('hospital_user', JSON.stringify(action.payload.user));
    },
    // Manual logout without API call (for expired tokens)
    clearAuth: (state) => {
      console.warn(
        `[LOGOUT-DEBUG] authSlice.clearAuth | reason=clearAuth_dispatched | ` +
        `user=${state.user?.username ?? 'null'} | userId=${state.user?.id ?? 'null'} | ` +
        `wasAuthenticated=${state.isAuthenticated} | timestamp=${new Date().toISOString()}`
      );
      console.trace('[LOGOUT-DEBUG] authSlice.clearAuth stack trace');
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.validationState = 'idle';
      state.error = null;
      state.retryCount = 0;
      state.lastValidation = null;
      state.networkError = false;
      state.hospital = null;
      state.accessibleHospitals = [];
      state.pendingLogoutRedirect = null;
      StorageService.clearAuthData();
    },
    setPendingLogoutRedirect: (state, action: PayloadAction<PendingLogoutRedirect>) => {
      state.pendingLogoutRedirect = action.payload;
    },
    clearPendingLogoutRedirect: (state) => {
      state.pendingLogoutRedirect = null;
    },
    // Reset retry count
    resetRetryCount: (state) => {
      state.retryCount = 0;
      state.networkError = false;
    },
    // Set validation state
    setValidationState: (state, action: PayloadAction<AuthState['validationState']>) => {
      state.validationState = action.payload;
    },
    setSessionSettings: (state, action: PayloadAction<SessionSettings | null>) => {
      state.sessionSettings = action.payload;
    },
    setHospitalContext: (state, action: PayloadAction<HospitalInfo | null>) => {
      state.hospital = action.payload;
    },
    // Refresh the switcher list mid-session (hospitals created after login
    // are invisible otherwise — the login response is only a snapshot).
    setAccessibleHospitals: (state, action: PayloadAction<HospitalInfo[]>) => {
      state.accessibleHospitals = action.payload || [];
      if (StorageService.setAccessibleHospitals) {
        StorageService.setAccessibleHospitals(action.payload || []);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.validationState = 'valid';
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.error = null;
        state.retryCount = 0;
        state.networkError = false;
        state.lastValidation = Date.now();
        state.sessionSettings = action.payload.session_settings ?? StorageService.getSessionSettings();
        // Successful login: drop any pending logout redirect & release the
        // LogoutCoordinator so future auth-failures can trigger a fresh logout.
        state.pendingLogoutRedirect = null;
        LogoutCoordinator.reset();
        if (DEBUG_LOGS && action.payload.session_settings) {
          const s = action.payload.session_settings;
          console.log(
            `[SESSION] loginAsync.fulfilled stored session_settings: idle_timeout_minutes=${s.idle_timeout_minutes ?? '?'} max_login_duration_minutes=${s.max_login_duration_minutes ?? '?'} session_check_interval_minutes=${s.session_check_interval_minutes ?? '?'}`
          );
        }
        // PHASE 5: Store hospital context
        state.hospital = action.payload.hospital || null;
        state.accessibleHospitals = action.payload.accessible_hospitals || [];
        // Store user and hospital data in localStorage for persistence
        localStorage.setItem('hospital_user', JSON.stringify(action.payload.user));
        if (action.payload.hospital && StorageService.setHospital) {
          StorageService.setHospital(action.payload.hospital);
        }
        if (action.payload.accessible_hospitals && StorageService.setAccessibleHospitals) {
          StorageService.setAccessibleHospitals(action.payload.accessible_hospitals);
        }
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        // Use the custom error message from rejectWithValue, fallback to generic message
        state.error = action.payload as string || action.error.message || 'Login failed';
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.validationState = 'idle';
        state.retryCount = 0;
        state.lastValidation = null;
        state.networkError = false;
        state.sessionSettings = null;
        state.hospital = null;
        state.accessibleHospitals = [];
        StorageService.clearAuthData();
      })
      .addCase(rehydrateAuth.pending, (state) => {
        state.loading = true;
        state.validationState = 'validating';
        state.error = null;
      })
      .addCase(rehydrateAuth.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.validationState = 'valid';
        state.loading = false;
        state.error = null;
        state.retryCount = 0;
        state.networkError = false;
        if (!action.payload.fromCache) {
          state.lastValidation = Date.now();
          // Update localStorage with fresh user data from server
          localStorage.setItem('hospital_user', JSON.stringify(action.payload.user));
        }
        state.sessionSettings = StorageService.getSessionSettings();
      })
      .addCase(rehydrateAuth.rejected, (state, action) => {
        state.loading = false;
        const error = action.payload as any;
        
        if (error?.isAuthError) {
          // Check if this is just an initial load with no auth data
          if (error?.isInitialLoad) {
            // For initial load, just set state to idle and don't show error
            state.validationState = 'idle';
            state.error = null; // Don't show error for normal initial state
          } else {
            // Genuine auth error after login attempt - clear everything
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            state.validationState = 'invalid';
            state.retryCount = 0;
            state.error = error?.message || 'Authentication validation failed';
            state.sessionSettings = null;
          }
        } else {
          // Network/server error - keep existing auth state but mark as failed
          state.validationState = 'failed';
          state.networkError = error?.isNetworkError || false;
          state.error = error?.message || 'Authentication validation failed';
          
          if (error?.isRetryable) {
            state.retryCount += 1;
          }
        }
      })
      .addCase(retryAuthValidation.pending, (state) => {
        state.loading = true;
        state.validationState = 'validating';
        state.error = null;
      });
  },
});

export const { clearError, setUser, clearAuth, resetRetryCount, setValidationState, setSessionSettings, completeTwoStepLogin, setHospitalContext, setAccessibleHospitals, setPendingLogoutRedirect, clearPendingLogoutRedirect } = authSlice.actions;
export default authSlice.reducer;
